"use client"

import { useId, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Typography } from "@/components/typography"
import { getAttribution, trackEvent } from "@/lib/attribution"

interface EngagementFormProps {
  /** Engagement model name included in the inquiry email, e.g. "Retainer". */
  model: string
  choiceLabel: string
  choices: string[]
  notesLabel: string
  notesPlaceholder: string
  submitLabel: string
}

export function EngagementForm({
  model,
  choiceLabel,
  choices,
  notesLabel,
  notesPlaceholder,
  submitLabel,
}: EngagementFormProps) {
  const uid = useId()
  const [choice, setChoice] = useState<string | null>(null)
  const [notes, setNotes] = useState("")
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [company, setCompany] = useState("")
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle")
  const [formStarted, setFormStarted] = useState(false)

  const markFormStart = () => {
    if (formStarted) return
    setFormStarted(true)
    trackEvent("form_start", { form_id: "engagement", engagement_model: model })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const newErrors: { name?: string; email?: string } = {}
    if (!name.trim()) newErrors.name = "Name is required"
    if (!email.trim()) newErrors.email = "Email is required"
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email"
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      trackEvent("form_error", {
        form_id: "engagement",
        fields: Object.keys(newErrors).join(","),
      })
      return
    }

    setIsSubmitting(true)
    setStatus("idle")
    try {
      const details = [
        choice ? { label: choiceLabel, value: choice } : null,
        notes.trim() ? { label: notesLabel, value: notes.trim() } : null,
      ].filter(Boolean)

      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          config: { engagement: { model, details } },
          contact: {
            name: name.trim(),
            email: email.trim(),
            company: company.trim() || undefined,
          },
          attribution: getAttribution(),
        }),
      })
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || "Failed to send message")
      }
      setStatus("success")
    } catch (error) {
      console.error("Submission error:", error)
      setStatus("error")
    } finally {
      setIsSubmitting(false)
    }
  }

  if (status === "success") {
    return (
      <div className="rounded-2xl border border-tk-teal/30 bg-white/60 p-8 space-y-3">
        <Typography variant="h3" as="h3" className="text-tk-teal">
          ✓ Got it.
        </Typography>
        <Typography variant="body" className="text-tk-slate/80">
          I&apos;ll get back to you within a business day. Want to skip ahead?
          Email{" "}
          <a
            href="mailto:hello@tallkarol.com"
            className="font-semibold text-tk-teal hover:underline"
          >
            hello@tallkarol.com
          </a>{" "}
          or call{" "}
          <a
            href="tel:+12165058192"
            className="font-semibold text-tk-teal hover:underline"
          >
            +1 (216) 505-8192
          </a>
          .
        </Typography>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      onFocusCapture={markFormStart}
      onClick={markFormStart}
      className="space-y-6 rounded-2xl border border-tk-slate/10 bg-white/60 p-6 sm:p-8"
    >
      <fieldset className="space-y-3">
        <legend className="text-sm font-medium text-tk-onyx font-ui">
          {choiceLabel}{" "}
          <span className="text-xs text-tk-slate/70">(optional)</span>
        </legend>
        <div className="flex flex-wrap gap-2">
          {choices.map((option) => {
            const isActive = choice === option
            return (
              <button
                key={option}
                type="button"
                aria-pressed={isActive}
                onClick={() => setChoice(isActive ? null : option)}
                className={`rounded-full px-4 py-2 text-sm font-medium font-ui transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tk-teal focus-visible:ring-offset-2 ${
                  isActive
                    ? "bg-tk-teal text-tk-linen"
                    : "border border-tk-slate/25 text-tk-slate hover:border-tk-teal hover:text-tk-onyx"
                }`}
              >
                {option}
              </button>
            )
          })}
        </div>
      </fieldset>

      <div className="space-y-2">
        <Label
          htmlFor={`${uid}-notes`}
          className="text-sm font-medium text-tk-onyx font-ui"
        >
          {notesLabel}{" "}
          <span className="text-xs text-tk-slate/70">(optional)</span>
        </Label>
        <Textarea
          id={`${uid}-notes`}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="min-h-[120px] border-tk-slate/20 bg-tk-linen"
          placeholder={notesPlaceholder}
        />
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div className="space-y-2">
          <Label
            htmlFor={`${uid}-name`}
            className="text-sm font-medium text-tk-onyx font-ui"
          >
            Name <span className="text-tk-teal">*</span>
          </Label>
          <Input
            id={`${uid}-name`}
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              if (errors.name) setErrors({ ...errors, name: undefined })
            }}
            className={`border-tk-slate/20 bg-tk-linen ${errors.name ? "border-tk-teal" : ""}`}
            placeholder="Your name"
          />
          {errors.name && (
            <p className="text-xs text-tk-teal font-body">{errors.name}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label
            htmlFor={`${uid}-email`}
            className="text-sm font-medium text-tk-onyx font-ui"
          >
            Email <span className="text-tk-teal">*</span>
          </Label>
          <Input
            id={`${uid}-email`}
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              if (errors.email) setErrors({ ...errors, email: undefined })
            }}
            className={`border-tk-slate/20 bg-tk-linen ${errors.email ? "border-tk-teal" : ""}`}
            placeholder="your@email.com"
          />
          {errors.email && (
            <p className="text-xs text-tk-teal font-body">{errors.email}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label
          htmlFor={`${uid}-company`}
          className="text-sm font-medium text-tk-onyx font-ui"
        >
          Company <span className="text-xs text-tk-slate/70">(optional)</span>
        </Label>
        <Input
          id={`${uid}-company`}
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          className="border-tk-slate/20 bg-tk-linen"
          placeholder="Your company name"
        />
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full rounded-full bg-tk-teal px-8 py-2 text-sm font-semibold text-tk-linen hover:bg-tk-teal/90 font-ui disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? "Sending..." : submitLabel}
      </Button>

      {status === "error" && (
        <div className="rounded-lg border border-tk-teal/20 bg-tk-teal/10 p-4">
          <Typography variant="body-sm" className="font-semibold text-tk-teal">
            Something went wrong. Please try again,{" "}
            <a
              href="mailto:hello@tallkarol.com?subject=Tall%20Karol%20Website%20Contact%20Request"
              className="underline"
            >
              email me directly
            </a>
            , or{" "}
            <a href="tel:+12165058192" className="underline">
              give me a call
            </a>
            .
          </Typography>
        </div>
      )}
    </form>
  )
}
